import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MapPin,
  Trash2,
  RotateCcw,
  Pencil,
  Plus,
  MessageSquare,
  MoreVertical,
  Camera,
  LinkIcon,
  ImageMinus,
} from 'lucide-react';
import type { PhotoRecordDto } from '@forest/contracts';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Button } from '@/components/ui/Button';
import {
  FilterCheckbox,
  FilterPanel,
  FilterToggleButton,
  PageActions,
  PageTitle,
  PageToolbar,
  StatStrip,
  useFilterPanel,
} from '@/components/ui/PageToolbar';
import { TablePagination } from '@/components/ui/TablePagination';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dialog } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '@/components/ui/DropdownMenu';
import { useUiStore } from '@/stores/uiStore';
import {
  usePhotos,
  usePhoto,
  usePhotoFacets,
  useDeletePhoto,
  useHardDeletePhoto,
  useRestorePhoto,
} from '@/features/photos/usePhotos';
import { PHOTO_QUERY_PARAM } from '@/features/photos/photoLink';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { PhotoFormDialog } from '@/features/photos/PhotoFormDialog';
import { PhotoUploadDialog } from '@/features/photos/PhotoUploadDialog';
import { PhotoViewModal } from '@/features/photos/PhotoViewModal';
import { canEditPhoto } from '@/features/photos/photoPermissions';
import { AssignParcelDialog } from '@/features/photos/AssignParcelDialog';
import { CommentsPanel } from '@/features/comments/CommentsPanel';
import { useAuthStore } from '@/stores/sessionStore';
import { PolygonStatusBadge } from '@/features/photos/PolygonStatusBadge';
import { SortableHeader } from '@/components/ui/SortableHeader';
import { POLYGON_STATUS_LABELS } from '@forest/contracts';

export function PhotoDatabasePage() {
  const { t, i18n } = useTranslation(['photos', 'common', 'comments', 'database']);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [raioane, setRaioane] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [hasParcel, setHasParcel] = useState<boolean | undefined>(undefined);
  const [polygonStatuses, setPolygonStatuses] = useState<string[]>([]);
  const [authorCods, setAuthorCods] = useState<string[]>([]);
  const [entitatiSilvice, setEntitatiSilvice] = useState<string[]>([]);
  /** Polygon-derived filters. The server matches these against the
   *  photo's own column OR its linked polygon's, except `projectId`
   *  (ID_Cod), which exists only on the polygon. */
  const [codProiecte, setCodProiecte] = useState<string[]>([]);
  const [ucfs, setUcfs] = useState<string[]>([]);
  const [projectId, setProjectId] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  /** Filters collapse at every breakpoint, not just on phones — the
   *  table is the point of the page. Remembered per browser. */
  const { open: filtersOpen, toggle: toggleFilters } = useFilterPanel(
    'forest.ui.photos.filters',
  );
  const activeFilterCount =
    (raioane.length > 0 ? 1 : 0) +
    (years.length > 0 ? 1 : 0) +
    (hasParcel !== undefined ? 1 : 0) +
    (polygonStatuses.length > 0 ? 1 : 0) +
    (authorCods.length > 0 ? 1 : 0) +
    (entitatiSilvice.length > 0 ? 1 : 0) +
    (codProiecte.length > 0 ? 1 : 0) +
    (ucfs.length > 0 ? 1 : 0) +
    (projectId.trim() ? 1 : 0) +
    (includeDeleted ? 1 : 0);
  type PhotoSort =
    | 'createdAt'
    | 'nr'
    | 'fileName'
    | 'raion'
    | 'anul'
    | 'year'
    | 'latitude'
    | 'longitude'
    | 'polygonStatus'
    | 'authorCod';
  const [sort, setSort] = useState<PhotoSort>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const onSort = (next: { sort: PhotoSort; order: 'asc' | 'desc' }) => {
    setPage(1);
    setSort(next.sort);
    setOrder(next.order);
  };

  const [mapPhoto, setMapPhoto] = useState<PhotoRecordDto | null>(null);

  /* `?photo=<id>` arrives from a polygon's map marker or photo list. The
     linked photo is usually not on the current page and need not match
     the active filters, so it is fetched on its own and shown directly.
     The param is dropped once the modal closes, leaving a normal table
     rather than a link that reopens on every refresh. */
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPhotoId = searchParams.get(PHOTO_QUERY_PARAM);
  const linkedPhoto = usePhoto(linkedPhotoId);
  useEffect(() => {
    if (linkedPhoto.data) setMapPhoto(linkedPhoto.data);
  }, [linkedPhoto.data]);
  const clearLinkedPhoto = useCallback(() => {
    setMapPhoto(null);
    if (!linkedPhotoId) return;
    const next = new URLSearchParams(searchParams);
    next.delete(PHOTO_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [linkedPhotoId, searchParams, setSearchParams]);
  const [formOpen, setFormOpen] = useState(false);
  const [formPhoto, setFormPhoto] = useState<PhotoRecordDto | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<PhotoRecordDto | null>(null);
  const [photoToHardDelete, setPhotoToHardDelete] = useState<PhotoRecordDto | null>(null);
  const [commentsPhoto, setCommentsPhoto] = useState<PhotoRecordDto | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(
    null,
  );
  const [assignPhoto, setAssignPhoto] = useState<PhotoRecordDto | null>(null);

  const currentUser = useAuthStore((s) => s.user);
  const userRole = currentUser?.role;
  // SUPER_ADMIN + CONTROLOR can re-assign ANY photo to ANY polygon
  // (server-side canMutate was broadened for them on 2026-05-19).
  // ADMINISTRATOR can re-assign their own photos.
  const canAssignParcel =
    userRole === 'SUPER_ADMIN' ||
    userRole === 'CONTROLOR' ||
    userRole === 'ADMINISTRATOR';
  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  const facets = usePhotoFacets();
  const deleteMut = useDeletePhoto();
  const hardDeleteMut = useHardDeletePhoto();
  const restoreMut = useRestorePhoto();
  const pushToast = useUiStore((s) => s.pushToast);

  const params = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      raioane: raioane.length > 0 ? raioane.join(',') : undefined,
      years: years.length > 0 ? years.join(',') : undefined,
      hasParcel,
      polygonStatuses: polygonStatuses.length > 0 ? polygonStatuses.join(',') : undefined,
      authorCods: authorCods.length > 0 ? authorCods.join(',') : undefined,
      entitatiSilvice:
        entitatiSilvice.length > 0 ? entitatiSilvice.join(',') : undefined,
      codProiecte: codProiecte.length > 0 ? codProiecte.join(',') : undefined,
      ucfs: ucfs.length > 0 ? ucfs.join(',') : undefined,
      projectId: projectId.trim() || undefined,
      includeDeleted: includeDeleted || undefined,
      sort,
      order,
    }),
    [
      page,
      limit,
      search,
      raioane,
      years,
      hasParcel,
      polygonStatuses,
      authorCods,
      entitatiSilvice,
      codProiecte,
      ucfs,
      projectId,
      includeDeleted,
      sort,
      order,
    ],
  );
  const query = usePhotos(params);

  const rows = query.data?.items ?? [];
  const totals = query.data?.totals;
  const pag = query.data?.pagination;

  const openCreateForm = () => {
    setFormPhoto(null);
    setFormOpen(true);
  };

  const [uploadOpen, setUploadOpen] = useState(false);

  const openEditForm = (p: PhotoRecordDto) => {
    setFormPhoto(p);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!photoToDelete) return;
    const label = photoToDelete.fileName;
    try {
      await deleteMut.mutateAsync(photoToDelete.id);
      setPhotoToDelete(null);
      pushToast({ variant: 'success', message: t('photos:delete.success', { label }) });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('photos:delete.failed', { message: (e as Error).message }),
      });
    }
  };

  const handleRestore = async (p: PhotoRecordDto) => {
    const label = p.fileName;
    try {
      await restoreMut.mutateAsync(p.id);
      pushToast({ variant: 'success', message: t('photos:restore.success', { label }) });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('photos:restore.failed', { message: (e as Error).message }),
      });
    }
  };

  const confirmHardDelete = async () => {
    if (!photoToHardDelete) return;
    const label = photoToHardDelete.fileName;
    try {
      await hardDeleteMut.mutateAsync(photoToHardDelete.id);
      setPhotoToHardDelete(null);
      pushToast({
        variant: 'success',
        message: t('photos:hardDelete.success', { label }),
      });
    } catch (e) {
      pushToast({
        variant: 'error',
        message: t('photos:hardDelete.failed', { message: (e as Error).message }),
      });
    }
  };

  return (
    /* Flex column fills <main>; the table card below scrolls
       internally so title + stats + filters stay fixed at top. */
    <div className="animate-fade-in flex h-full min-h-0 flex-col gap-2">
      {/* Row 1: title + inline stats + actions. */}
      <PageToolbar>
        <PageTitle title={t('photos:page.title')} hint={t('photos:page.subtitle')} />
        {totals ? (
          <StatStrip
            items={[
              {
                label: t('photos:stats.totalPhotos'),
                value: totals.totalPhotos.toLocaleString('ro-MD'),
                icon: Camera,
                color: 'emerald',
              },
              {
                label: t('photos:stats.withParcel'),
                value: totals.withParcel.toLocaleString('ro-MD'),
                icon: LinkIcon,
                color: 'sky',
              },
              {
                label: t('photos:stats.withoutParcel'),
                value: totals.withoutParcel.toLocaleString('ro-MD'),
                icon: ImageMinus,
                color: 'amber',
              },
            ]}
          />
        ) : null}
        <PageActions>
          <Button size="sm" variant="secondary" onClick={openCreateForm}>
            <Plus className="h-3.5 w-3.5" />
            {t('photos:page.newButton')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
            {t('photos:page.uploadButton', { defaultValue: 'Încarcă foto' })}
          </Button>
          <Link to="/app/import?target=photo">
            <Button size="sm">{t('photos:page.importButton')}</Button>
          </Link>
        </PageActions>
      </PageToolbar>

      {/* Row 2: search + filter toggle. The filter grid below is
          collapsed by default at every breakpoint (state persisted) so
          the table keeps the vertical space on small monitors. */}
      <div className="flex items-center gap-2">
        <Input
          uiSize="sm"
          className="flex-1"
          placeholder={t('photos:filters.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <FilterToggleButton
          open={filtersOpen}
          count={activeFilterCount}
          onClick={toggleFilters}
          label={t('common:action.filters')}
        />
      </div>

      {filtersOpen ? (
        <FilterPanel className="xl:grid-cols-7">
          <MultiSelect
            uiSize="sm"
            placeholder={t('photos:filters.allRaioane')}
            countLabel={(n) => t('photos:filters.raioaneSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={raioane}
            onChange={(next) => {
              setPage(1);
              setRaioane(next);
            }}
            options={[
              ...(facets.data?.hasNullRaion
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.raioane ?? []).map((r) => ({ value: r, label: r })),
            ]}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('photos:filters.allAni')}
            countLabel={(n) => t('photos:filters.aniSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={years}
            onChange={(next) => {
              setPage(1);
              setYears(next);
            }}
            options={[
              ...(facets.data?.hasNullYear
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.ani ?? []).map((a) => ({
                value: String(a),
                label: String(a),
              })),
            ]}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('photos:filters.allStatuses')}
            countLabel={(n) => t('photos:filters.statusesSelected', { count: n })}
            clearLabel={t('photos:filters.clearSelection')}
            value={polygonStatuses}
            onChange={(next) => {
              setPage(1);
              setPolygonStatuses(next);
            }}
            options={Object.entries(POLYGON_STATUS_LABELS).map(([key, labels]) => ({
              value: key,
              label: labels[i18n.language as 'ro' | 'ru' | 'en'] ?? labels.ro,
            }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('photos:filters.allAuthors', { defaultValue: 'Toți autorii' })}
            countLabel={(n) =>
              t('photos:filters.authorsSelected', {
                count: n,
                defaultValue: '{{count}} autori',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={authorCods}
            onChange={(next) => {
              setPage(1);
              setAuthorCods(next);
            }}
            options={(facets.data?.authors ?? [])
              .filter((a) => a.cod)
              .map((a) => ({
                value: a.cod as string,
                label: a.fullName ? `${a.cod} — ${a.fullName}` : (a.cod as string),
              }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('photos:filters.allEntitatiSilvice', {
              defaultValue: 'Toate entitățile silvice',
            })}
            countLabel={(n) =>
              t('photos:filters.entitatiSilviceSelected', {
                count: n,
                defaultValue: '{{count}} entități',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={entitatiSilvice}
            onChange={(next) => {
              setPage(1);
              setEntitatiSilvice(next);
            }}
            options={[
              ...(facets.data?.hasNullEntitateSilvica
                ? [{ value: '', label: t('photos:filters.unassigned') }]
                : []),
              ...(facets.data?.entitatiSilvice ?? []).map((e) => ({
                value: e,
                label: e,
              })),
            ]}
          />
          {/* Polygon-derived filters. A linked photo's own copies of
              these are nulled server-side, so the options come from the
              linked polygons as well as the photos themselves. */}
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.codProiectPlaceholder', {
              defaultValue: 'Cod proiect',
            })}
            countLabel={(n) =>
              t('database:filters.codProiectSelected', {
                count: n,
                defaultValue: '{{count}} coduri',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={codProiecte}
            onChange={(next) => {
              setPage(1);
              setCodProiecte(next);
            }}
            options={(facets.data?.codProiecte ?? []).map((c) => ({
              value: c,
              label: c,
            }))}
          />
          <MultiSelect
            uiSize="sm"
            placeholder={t('database:filters.ucfPlaceholder', {
              defaultValue: 'UCF',
            })}
            countLabel={(n) =>
              t('database:filters.ucfSelected', {
                count: n,
                defaultValue: '{{count}} UCF',
              })
            }
            clearLabel={t('photos:filters.clearSelection')}
            value={ucfs}
            onChange={(next) => {
              setPage(1);
              setUcfs(next);
            }}
            options={(facets.data?.ucfs ?? []).map((u) => ({ value: u, label: u }))}
          />
          <Input
            uiSize="sm"
            placeholder={t('photos:filters.projectIdPlaceholder', {
              defaultValue: 'ID proiect',
            })}
            aria-label={t('photos:filters.projectIdPlaceholder', {
              defaultValue: 'ID proiect',
            })}
            value={projectId}
            onChange={(e) => {
              setPage(1);
              setProjectId(e.target.value);
            }}
          />
          <FilterCheckbox
            checked={hasParcel === true}
            onChange={(next) => {
              setPage(1);
              setHasParcel(next ? true : undefined);
            }}
            label={t('photos:filters.hasParcel')}
          />
          <FilterCheckbox
            checked={includeDeleted}
            onChange={(next) => {
              setPage(1);
              setIncludeDeleted(next);
            }}
            label={t('photos:filters.includeDeleted')}
          />
        </FilterPanel>
      ) : null}

      <Card className="flex flex-1 min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-auto px-2 pb-2">
          {query.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Spinner /> {t('state.loading')}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('photos:empty.title')}
              description={t('photos:empty.description')}
            />
          ) : (
            <>
            {/* Mobile cards */}
            <div className="space-y-2 pt-2 md:hidden">
              {rows.map((r) => {
                const isDeleted = r.deletedAt !== null;
                return (
                  <div
                    key={r.id}
                    className={
                      isDeleted
                        ? 'flex gap-3 rounded-lg border border-red-200 bg-red-50/40 p-3 text-slate-400 line-through'
                        : 'flex gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm'
                    }
                  >
                    {r.downloadUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLightbox({ url: toViewUrl(r.downloadUrl), name: r.fileName })
                        }
                        className="flex-shrink-0"
                      >
                        <img
                          src={toThumbnailUrl(r.downloadUrl)}
                          alt={r.fileName}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          className="h-16 w-16 rounded border border-slate-200 bg-slate-100 object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </button>
                    ) : (
                      <div className="h-16 w-16 flex-shrink-0 rounded border border-slate-200 bg-slate-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="flex items-baseline gap-1.5 text-sm font-semibold text-slate-900">
                        <span className="font-mono text-xs text-slate-400">#{r.nr}</span>
                        <span className="truncate" title={primarieRaionCod(r)}>
                          {primarieRaionCod(r)}
                        </span>
                      </p>
                      <div className="mt-1">
                        <PolygonStatusBadge status={r.polygonStatus} />
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <p className="truncate text-xs text-slate-500">
                          {r.author?.cod ?? r.author?.fullName ?? '—'}
                          {r.anul ? ` · ${r.anul}` : ''}
                        </p>
                        <button
                          type="button"
                          onClick={() => setCommentsPhoto(r)}
                          className="inline-flex flex-shrink-0 items-center gap-1 rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-forest-50 hover:text-forest-700"
                          title={t('photos:action.comments')}
                          aria-label={t('photos:action.comments')}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          <span className="font-semibold tabular-nums">{r.commentCount}</span>
                        </button>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownTrigger>
                        <MoreVertical className="h-4 w-4" />
                      </DropdownTrigger>
                      <DropdownContent align="end">
                        <DropdownItem
                          icon={MapPin}
                          label={t('photos:action.map')}
                          onClick={() => setMapPhoto(r)}
                        />
                        <DropdownSeparator />
                        {isDeleted ? (
                          <>
                            <DropdownItem
                              icon={RotateCcw}
                              label={t('photos:action.restore')}
                              onClick={() => void handleRestore(r)}
                              disabled={restoreMut.isPending}
                            />
                            {isSuperAdmin ? (
                              <DropdownItem
                                icon={Trash2}
                                label={t('photos:action.hardDelete')}
                                onClick={() => setPhotoToHardDelete(r)}
                                destructive
                              />
                            ) : null}
                          </>
                        ) : (
                          <>
                            {canEditPhoto(currentUser, r) ? (
                              <DropdownItem
                                icon={Pencil}
                                label={t('photos:action.edit')}
                                onClick={() => openEditForm(r)}
                              />
                            ) : null}
                            <DropdownItem
                              icon={Trash2}
                              label={t('photos:action.delete')}
                              onClick={() => setPhotoToDelete(r)}
                              destructive
                            />
                          </>
                        )}
                      </DropdownContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
            {/* No own scrollport here: the card body div is the scroll container,
                so the sticky thead anchors to it and stays fixed. An
                inner overflow-auto would capture the stickiness in a
                wrapper that never scrolls, letting the header slide away. */}
            <div className="hidden md:block">
              <table className="table-striped table-sticky w-full text-sm dark:text-slate-300">
                <thead className="bg-slate-50/95 text-xs uppercase text-slate-500 backdrop-blur-sm dark:bg-slate-800/95 dark:text-slate-400">
                  <tr>
                    <SortableHeader<PhotoSort>
                      field="nr"
                      current={sort}
                      order={order}
                      onSort={onSort}
                      defaultOrder="desc"
                      align="right"
                      className="w-16"
                    >
                      {t('photos:table.header.nr')}
                    </SortableHeader>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.primarieRaionCod', {
                        defaultValue: 'Primarii_Raion_Cod',
                      })}
                    </th>
                    <th
                      className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500"
                      title={t('photos:table.header.gpsAccuracyHint', {
                        defaultValue: 'GPS accuracy radius (meters)',
                      })}
                    >
                      {t('photos:table.header.gpsAccuracy', {
                        defaultValue: 'GPS ±m',
                      })}
                    </th>
                    <SortableHeader<PhotoSort>
                      field="raion"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('photos:table.header.parcel')}
                    </SortableHeader>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.ucf')}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-right text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.suprafata')}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.subParcel')}
                    </th>
                    <SortableHeader<PhotoSort>
                      field="polygonStatus"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('photos:table.header.status')}
                    </SortableHeader>
                    <SortableHeader<PhotoSort>
                      field="year"
                      current={sort}
                      order={order}
                      onSort={onSort}
                      defaultOrder="desc"
                      align="right"
                    >
                      {t('photos:table.header.anul')}
                    </SortableHeader>
                    <SortableHeader<PhotoSort>
                      field="authorCod"
                      current={sort}
                      order={order}
                      onSort={onSort}
                    >
                      {t('photos:table.header.author', { defaultValue: 'Autor' })}
                    </SortableHeader>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.entitateSilvica', {
                        defaultValue: 'Entitate silvică',
                      })}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.speciaDeBaza', { defaultValue: 'SB' })}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-right text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.anulCultura', { defaultValue: 'An cultură' })}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-left text-slate-500 dark:text-slate-400">
                      {t('photos:table.header.ultimaLucrare', { defaultValue: 'Ultima lucrare' })}
                    </th>
                    <th className="px-3 py-2 text-xs uppercase font-semibold text-center text-slate-500 dark:text-slate-400">{t('photos:table.header.photo')}</th>
                    <th className="w-12 px-2 py-2 text-xs uppercase font-semibold text-center text-slate-500 dark:text-slate-400">{t('photos:table.header.comments', { defaultValue: 'Comentarii' })}</th>
                    <th className="w-12 px-2 py-2 text-xs uppercase font-semibold text-center text-slate-500 dark:text-slate-400">{t('photos:table.header.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isDeleted = r.deletedAt !== null;
                    return (
                      <tr
                        key={r.id}
                        className={
                          isDeleted
                            ? 'border-t border-slate-100 bg-red-50/40 text-slate-400 line-through'
                            : 'border-t border-slate-100 transition-colors hover:bg-forest-50/40'
                        }
                      >
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-slate-500 dark:text-slate-400">
                          {r.nr}
                        </td>
                        <td
                          className="px-3 py-1.5 max-w-[220px] truncate"
                          title={primarieRaionCod(r)}
                        >
                          {primarieRaionCod(r)}
                        </td>
                        <td
                          className={`px-3 py-1.5 text-right font-mono text-xs tabular-nums ${
                            r.gpsAccuracyM == null
                              ? 'text-slate-300'
                              : r.gpsAccuracyM <= 8
                                ? 'text-emerald-600'
                                : r.gpsAccuracyM <= 20
                                  ? 'text-amber-600'
                                  : 'text-red-600'
                          }`}
                          title={
                            r.gpsAccuracyM != null
                              ? `±${r.gpsAccuracyM.toFixed(1)} m`
                              : ''
                          }
                        >
                          {r.gpsAccuracyM != null
                            ? `±${Math.round(r.gpsAccuracyM)}`
                            : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {r.parcelId ? (
                            <Link
                              to={`/app/parcels?search=${encodeURIComponent(r.parcelId)}`}
                              className="text-forest-700 underline hover:text-forest-800"
                              title={r.parcelId}
                            >
                              {r.parcelLabel ?? r.parcelId.slice(0, 8) + '...'}
                            </Link>
                          ) : canAssignParcel && !isDeleted && canEditPhoto(currentUser, r) ? (
                            <button
                              type="button"
                              onClick={() => setAssignPhoto(r)}
                              className="text-xs text-amber-700 underline hover:text-amber-800"
                            >
                              {t('photos:assign.button')}
                            </button>
                          ) : (
                            '\u2014'
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-600">
                          {polygonMeta(r).ucf ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-slate-600">
                          {r.parcel ? r.parcel.suprafata.toFixed(2) : '\u2014'}
                        </td>
                        <td
                          className="px-3 py-1.5 font-mono text-xs font-semibold text-orange-700"
                          title={
                            r.subParcel ? `${r.subParcel.suprafata.toFixed(2)} ha` : undefined
                          }
                        >
                          {r.subParcel?.ucf ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5">
                          <PolygonStatusBadge status={r.polygonStatus} />
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">{r.anul ?? '\u2014'}</td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-600" title={r.author?.fullName ?? ''}>
                          {r.author?.cod ?? r.author?.fullName ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-600">
                          {polygonMeta(r).entitateSilvica ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-600">
                          {r.speciaDeBaza ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-slate-600">
                          {r.anulCultura ?? '\u2014'}
                        </td>
                        <td
                          className="px-3 py-1.5 max-w-[200px] truncate text-xs text-slate-600"
                          title={r.ultimaLucrare ?? ''}
                        >
                          {r.ultimaLucrare ?? '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {r.downloadUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                          setLightbox({ url: toViewUrl(r.downloadUrl), name: r.fileName })
                        }
                              className="inline-block rounded-md border border-slate-200 p-0.5 hover:border-forest-400 hover:shadow-sm transition-all"
                              title={t('photos:action.viewPhoto')}
                            >
                              <img
                                src={toThumbnailUrl(r.downloadUrl)}
                                alt={r.fileName}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                className="h-10 w-10 rounded object-cover bg-slate-100"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </button>
                          ) : (
                            '\u2014'
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => setCommentsPhoto(r)}
                            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-slate-500 transition-colors hover:bg-forest-50 hover:text-forest-700 dark:text-slate-400 dark:hover:bg-forest-900/20 dark:hover:text-forest-300"
                            title={t('photos:action.comments')}
                            aria-label={t('photos:action.comments')}
                          >
                            <MessageSquare className="h-4 w-4" />
                            <span className="text-xs font-semibold tabular-nums">
                              {r.commentCount}
                            </span>
                          </button>
                        </td>
                        <td className="px-2 py-1.5 text-center no-underline">
                          <DropdownMenu>
                            <DropdownTrigger>
                              <MoreVertical className="h-4 w-4" />
                            </DropdownTrigger>
                            <DropdownContent align="end">
                              <DropdownItem
                                icon={MapPin}
                                label={t('photos:action.map')}
                                onClick={() => setMapPhoto(r)}
                              />
                              <DropdownSeparator />
                              {isDeleted ? (
                                <>
                                  <DropdownItem
                                    icon={RotateCcw}
                                    label={t('photos:action.restore')}
                                    onClick={() => void handleRestore(r)}
                                    disabled={restoreMut.isPending}
                                  />
                                  {isSuperAdmin ? (
                                    <DropdownItem
                                      icon={Trash2}
                                      label={t('photos:action.hardDelete')}
                                      onClick={() => setPhotoToHardDelete(r)}
                                      destructive
                                    />
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  {canEditPhoto(currentUser, r) ? (
                                    <DropdownItem
                                      icon={Pencil}
                                      label={t('photos:action.edit')}
                                      onClick={() => openEditForm(r)}
                                    />
                                  ) : null}
                                  <DropdownItem
                                    icon={Trash2}
                                    label={t('photos:action.delete')}
                                    onClick={() => setPhotoToDelete(r)}
                                    destructive
                                  />
                                </>
                              )}
                            </DropdownContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
        {/* Outside the scroll container and never gated on `pag`: the pager
            and the rows-per-page select stay pinned to the bottom of the
            card instead of sitting 200 rows down. */}
        <TablePagination
          className="border-t border-slate-200 px-2 py-1.5 dark:border-slate-700"
          page={page}
          limit={limit}
          total={pag?.total ?? 0}
          totalPages={pag?.totalPages ?? 1}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setPage(1);
            setLimit(next);
          }}
          rowsLabel={t('common:common.rows')}
          prevLabel={t('common:pagination.prev')}
          nextLabel={t('common:pagination.next')}
        />
      </Card>

      <PhotoViewModal
        canEdit={mapPhoto ? canEditPhoto(currentUser, mapPhoto) : false}
        open={mapPhoto !== null}
        photo={mapPhoto}
        onClose={clearLinkedPhoto}
      />

      <PhotoFormDialog
        open={formOpen}
        photo={formPhoto}
        onClose={() => setFormOpen(false)}
      />

      <PhotoUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      <AssignParcelDialog
        photo={assignPhoto}
        onClose={() => setAssignPhoto(null)}
      />

      {/* Photo comments dialog */}
      <Dialog
        open={commentsPhoto !== null}
        onClose={() => setCommentsPhoto(null)}
        title={t('comments:title')}
        size="lg"
      >
        {commentsPhoto ? (
          <CommentsPanel entityType="PhotoRecord" entityId={commentsPhoto.id} />
        ) : null}
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={photoToDelete !== null}
        onClose={() => {
          if (!deleteMut.isPending) setPhotoToDelete(null);
        }}
        title={t('photos:delete.confirmTitle')}
        size="md"
      >
        {photoToDelete ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{t('photos:delete.confirmBody')}</p>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="font-semibold text-slate-900">{photoToDelete.fileName}</div>
              <div className="text-xs text-slate-600">
                {photoToDelete.latitude.toFixed(6)}, {photoToDelete.longitude.toFixed(6)}
                {photoToDelete.raion ? ` · ${photoToDelete.raion}` : ''}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setPhotoToDelete(null)}
                disabled={deleteMut.isPending}
              >
                {t('photos:delete.cancel')}
              </Button>
              <Button
                onClick={() => void confirmDelete()}
                loading={deleteMut.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('photos:delete.confirm')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      {/* Hard-delete (permanent) — SUPER_ADMIN only. Trigger gated in
          the action menu. */}
      <Dialog
        open={photoToHardDelete !== null}
        onClose={() => {
          if (!hardDeleteMut.isPending) setPhotoToHardDelete(null);
        }}
        title={t('photos:hardDelete.confirmTitle')}
        size="md"
      >
        {photoToHardDelete ? (
          <div className="space-y-4">
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/40">
              <p className="font-semibold text-red-700 dark:text-red-300">
                {t('photos:hardDelete.warningTitle')}
              </p>
              <p className="mt-1 text-red-700 dark:text-red-300">
                {t('photos:hardDelete.warningBody')}
              </p>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                {photoToHardDelete.fileName}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                {photoToHardDelete.latitude.toFixed(6)},{' '}
                {photoToHardDelete.longitude.toFixed(6)}
                {photoToHardDelete.raion ? ` · ${photoToHardDelete.raion}` : ''}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setPhotoToHardDelete(null)}
                disabled={hardDeleteMut.isPending}
              >
                {t('photos:hardDelete.cancel')}
              </Button>
              <Button
                onClick={() => void confirmHardDelete()}
                loading={hardDeleteMut.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('photos:hardDelete.confirm')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      {/* Lightbox */}
      <ImageLightbox
        url={lightbox?.url ?? null}
        alt={lightbox?.name}
        caption={lightbox?.name}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
}

/**
 * Composite "Primarii_Raion_Cod" label for the photo table — replaces the
 * raw file name. Joins the photo's primarie / raion / codProiect with
 * underscores, skipping any missing part; falls back to "—" when all
 * three are null.
 */
function primarieRaionCod(p: PhotoRecordDto): string {
  const loc = polygonMeta(p);
  const parts = [loc.primarie, loc.raion, loc.codProiect].filter(
    (v): v is string => !!v && v.trim().length > 0,
  );
  return parts.length > 0 ? parts.join('_') : '—';
}

/**
 * Location metadata for display.
 *
 * The linked polygon wins: a photo's own ucf / raion / primarie /
 * ocolSilvic / entitateSilvica / codProiect are deliberately nulled when
 * a parcel is attached (the edit dialog inherits them from the parcel),
 * so reading the photo's columns showed "—" for exactly the photos that
 * have the most context. Unlinked photos — mobile captures outside any
 * polygon — still fall back to their own values.
 */
export function polygonMeta(p: PhotoRecordDto) {
  return {
    ucf: p.parcel?.ucf ?? p.ucf,
    raion: p.parcel?.raion ?? p.raion,
    primarie: p.parcel?.primarie ?? p.primarie,
    codProiect: p.parcel?.codProiect ?? p.codProiect,
    ocolSilvic: p.parcel?.ocolSilvic ?? p.ocolSilvic,
    entitateSilvica: p.parcel?.entitateSilvica ?? p.entitateSilvica,
    /** True when the values above came from the polygon, not the photo. */
    fromPolygon: p.parcel !== null,
  };
}

/**
 * Convert a Google Drive download URL to a thumbnail URL.
 * Input:  https://drive.google.com/uc?id=FILE_ID&export=download
 * Output: https://drive.google.com/thumbnail?id=FILE_ID&sz=w100
 */
function toThumbnailUrl(url: string): string {
  const match = /[?&]id=([^&]+)/.exec(url);
  if (match) return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w100`;
  // Fallback: use the URL itself as an image src (works for direct image URLs)
  return url;
}

/**
 * Convert a Google Drive download URL to a viewable (large) image URL.
 * Input:  https://drive.google.com/uc?id=FILE_ID&export=download
 * Output: https://drive.google.com/thumbnail?id=FILE_ID&sz=w1200
 */
function toViewUrl(url: string): string {
  const match = /[?&]id=([^&]+)/.exec(url);
  if (match) return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
  return url;
}
